/**
 * Email-tab hooks: an agent's mailbox settings and the instance's allowance.
 */

import { type AgentEmailPatch, hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

export function useAgentEmailQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.agentEmail(agentId),
    queryFn: () => api.email.get(agentId),
    enabled: options.enabled ?? true,
  });
}

/**
 * The quota is instance-wide, not per agent, but it is read on this agent's
 * behalf so a viewer without access to the agent cannot learn the tenant's
 * sending volume.
 */
export function useAgentEmailQuotaQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.agentEmailQuota(agentId),
    queryFn: () => api.email.quota(agentId),
    enabled: options.enabled ?? true,
    refetchInterval: 60_000,
  });
}

export function useUpdateAgentEmailMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: AgentEmailPatch) => api.email.update(agentId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agentEmail(agentId) });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.agentEmailQuota(agentId) });
    },
  });
}
